import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AgentAssistStatus,
  DigitalChannelType,
  MediaType,
  VoiceContactStatus,
  VoiceAuthenticationStatus,
  contactButtons,
} from '@nice-ccf/core-sdk';
import {
  Agent,
  AgentGroup,
  AgentStatus,
  contactHistoryListData,
  interactionsHistoryListData,
} from '@nice-ccf/ui-components';
import { enableMapSet } from 'immer';
import { notesHistoryListData } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-consumer-card/ccf-consumer-card-notes-tab/mock-data/notes-history-mock-data';
import { Note } from 'libs/react/ui-components/src/lib/ccf-inbox/model/notes';
import { customCopilotTemplate } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/custom-flow';
import { customCopilotVoiceTemplate } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/copilot-voice-custom-flow';
import { customCopilotEmailTemplate } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/copilot-email-custom-flow';
import { externalTabDetails } from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-consumer-card/ccf-consumer-card-external-data-tab/mock-data/externalTab-mock-data';

enableMapSet();

export const SIMULATOR_FEATURE_KEY = 'simulator';

export interface Customer {
  accountNumber: string;
  salutation?: string;
  title?: string;
  firstName: string;
  lastName: string;
  middleName: string;
  ssn: string;
  customerPrimaryAddress?: any;
  customerSecondaryAddress?: any;
  customerHistory: CustomerHistory[];
  phoneNo: string;
  emailId: string;
  replywithApproval?: string;
  twitterId: string;
  nextPage?: number;
  ibHistoryCount?: number;
  obHistoryCount?: number;
  otherHistoryCount?: number;
  nextPayment?: string;
  customerSince?: string;
  dateOfBirth?: string;
  balance?: string;
  accountLevel?: string;
  others?: any;
  externalDataLoyaltyInfoHide?: boolean;
  consumerCardTicketTabHide?: boolean;
}

export interface assignedAgent {
  agentId: string;
  agentName: string;
  agentStatus: AgentStatus;
  agentTeam: string;
  agentDepartment: string;
}

export interface CustomerHistory {
  id?: string;
  contactId: string;
  contactRequest: string;
  contactTrackingId: string;
  contactName: string;
  contactStatus: string;
  digitalChannel: string;
  isOutbound: boolean;
  updatedDate: string;
}

export interface interaction {
  id: number;
  crmIcon?: string;
  crmUrl?: string;
  status?: string;
  label?: string;
  date?: string;
  details?: string;
  recordType?: string;
  isCopy?: boolean;
}

export interface Contact {
  id: number;
  customer: Customer;
  messageType: string;
  queueName: string;
  recentInteraction: Array<interaction>;
  media:
    | MediaType.VOICE
    | MediaType.DIGITAL
    | MediaType.VOICEMAIL
    | MediaType.VIDEOCALL;
  channel?: string;
  channelContent?: string;
  contactStatus: string;
  contactButtons: contactButtons[];
  copilotAssist?: string;
  copilotSource?: string;
  liveVoice?: string;
  copilotUserContext?: string;
  assignedAgent?: assignedAgent;
  agentAssistStatus?:
    | AgentAssistStatus.NEGATIVE
    | AgentAssistStatus.POSITIVE
    | AgentAssistStatus.INTERMEDIATE;
  customerHistory?: CustomerHistory[];
  lastUpdated?: string;
  rtaStatus?:
    | VoiceAuthenticationStatus.AUTHENTICATED
    | VoiceAuthenticationStatus.NOTAUTHENTICATED
    | VoiceAuthenticationStatus.MISSINGPATRONID;
  outBoundInitiatedForVoiceMail?: boolean;
  screenPopURL?: string;
  screenPopPreference?: string;
  autoSummary?: string;
  autoSummaryText?: string;
  customerChatReply?: string;
  journeyHistory?: boolean;
  perviousInteractionJsonData?: any;
  rtSummary?: string;
  autopilotSummary?: string;
  transferSummary?: string;
  showtransferSummary?: boolean;
  intents?: any;
  customReplies?: any;
  crmLabel?: string;
  crmUrl?: string;
  dateCreated: Date;
  lastVisited: Date;
  isElevated?: boolean;
  setUpForTransfer?: boolean;
  contactHistoryList?: any;
  interactionHistoryList?: any;
  elevationDetails?: any;
  botTranscript?: boolean;
  confidence?:[];
  botTranscriptData?: any;
  notes?: Array<Note>;
  externalData?: any;
  emailSubject?: any;
  receiverEmail?: string;
  attachments?: [];
}

export interface SimulatorState {
  contact: Array<Contact>;
  selectedContactIndex: number;
  modalMessage: string;
  agentDirectory: {
    myTeam: Agent[];
    favourites: Agent[];
    agentGroups: AgentGroup[];
    people: Customer[];
  };
  quickReplies: {
    allQReplies: QuickReply[];
  };
  customerChatInputReply?: any;
  copilotConfig?: any;
  indexConfig?: any;
  expertSystemOptions?: ExpertSystemOptions;
}

export interface AssociatedFile {
  name: string;
  description: string | null;
  lastUpdatedBy: string | null;
  lastUpdatedTime: string | null;
  status: string;
}

export interface ExpertSystemOptions {
  [key: string]: {
    expertSystem: string;
    expertCopilotURL: string;
    associatedFiles: AssociatedFile[]
    kBAnswersPath: string;
    salesOffersPath: string;
    compliancePath: string;
    expertCopilotClientKey: string;
    expertCopilotClientSecret: string;
    expertPathName: string;
    copilotPersona?: string;
    demoType: string;
    status: string;
    visibility: string;
    owner: string | null;
    description: string | null;
    lastUpdatedBy: string | null;
    lastUpdatedTime: string | null;
  };
}

export interface QuickReply {
  id: number;
  title: string;
  content1: string;
  content2?: string;
  isfavorite: boolean;
  isConfirmationNumberReq: boolean;
}

const initialSimulatorState: SimulatorState = {
  contact: [
    {
      id: 845932153,
      customer: {
        externalDataLoyaltyInfoHide: true,
        consumerCardTicketTabHide: true,
        accountNumber: '6868458454',
        salutation: 'Mr.',
        title: '',
        firstName: 'Liam',
        middleName: '',
        lastName: 'Davis',
        ssn: '63846838777',
        customerPrimaryAddress: {
          address1: '123445 Anystreet',
          address2: 'Avenue #23455',
          city: ' Anytown',
          state: 'OH',
          zip: '23345-1123',
          country: 'USA',
        },
        customerSecondaryAddress: {
          address1: '123445 Anystreet',
          address2: 'Avenue #23455',
          city: 'Anytown',
          state: 'OH',
          zip: '23345-1123',
          country: 'USA',
        },
        phoneNo: '253-895-8956',
        emailId: 'DavisBRLiam342@sunsyst.com',
        replywithApproval: 'No',
        twitterId: '@LiamDavis',
        balance: '$456.98',
        nextPayment: '$50',
        accountLevel: 'Basic',
        customerSince: '2/13/2024',
        dateOfBirth: '12/11/1989',
        customerHistory: [
          {
            id: '0',
            contactId: '253-895-8956',
            contactRequest: 'Airline Customer Service',
            contactTrackingId: '423-INCL-0246',
            contactName: 'Agent Smith',
            contactStatus: 'New',
            digitalChannel: 'digital',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '1',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Question about my luggage',
            contactTrackingId: '78-EMAIL-5S98',
            contactName: 'Agent Kapoor',
            contactStatus: 'Open',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '2',
            contactId: '253-895-8956',
            contactRequest: 'Customer Service',
            contactTrackingId: '897-OTVO-7455',
            contactName: 'Agent Tiwari',
            contactStatus: 'Pending',
            digitalChannel: 'voice',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '3',
            contactId: '253-895-8956',
            contactRequest: 'Cell Phone Number Edit',
            contactTrackingId: '',
            contactName: 'Agent Pablo Fransico',
            contactStatus: 'Resolved',
            digitalChannel: 'voice',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '4',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Tickets Purchased',
            contactTrackingId: '456-EMAIL-6245',
            contactName: 'Agent Winston',
            contactStatus: 'Escalated',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '5',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Channel added',
            contactTrackingId: '',
            contactName: 'Agent Tiwari',
            contactStatus: 'Closed',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '6',
            contactId: '360-253-8956',
            contactRequest: 'Customer added',
            contactTrackingId: '',
            contactName: 'Agent Dhaksan',
            contactStatus: 'Closed',
            digitalChannel: 'contactprofile',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
        ],
        others: {
          guarantorFirstName: 'Liam',
          guarantorLastName: 'Davis',
          contactToSpeak: '',
          contactGroup: 'Travel',
          department: 'Customer Service',
        },
      },
      notes: notesHistoryListData,
      externalData: externalTabDetails,
      recentInteraction: [
        {
          id: 20001,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          status: '4 - Low',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/09/2024 08:10 PM',
          recordType: 'current_record',
          details:
            'Customer called in about their hotel reservation to see if they were eligible for a free upgrade.',
        },
        {
          id: 20002,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'related_record',
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20003,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'related_record',
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20004,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'related_record',
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
      ],
      messageType: '253-895-8956',
      queueName: 'Cust. Service Request',
      media: MediaType.VOICE,
      contactStatus: VoiceContactStatus.RINGING,
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      copilotAssist: 'Yes',
      copilotSource: 'JSON',
      agentAssistStatus: AgentAssistStatus.NEGATIVE,
      contactButtons: [
        contactButtons.hold,
        contactButtons.mask,
        contactButtons.mute,
        contactButtons.keypad,
        contactButtons.transfer,
        contactButtons.record,
        //contactButtons.commit,
        contactButtons.hungup,
      ],
      screenPopURL: 'https://help.nice-incontact.com/content/home.htm#',
      screenPopPreference: 'Off',
      autoSummary: 'On',
      autoSummaryText: customCopilotVoiceTemplate?.finalAutoSummary,
      rtSummary: customCopilotVoiceTemplate?.rtSummary,
      journeyHistory: true,
      perviousInteractionJsonData: customCopilotVoiceTemplate?.journeyHistory,
      showtransferSummary: true,
      transferSummary: customCopilotVoiceTemplate?.agentTransferSummary,
      crmLabel: 'View CRM Details',
      crmUrl: 'http://www.crm.com',
      customerChatReply: 'kkkkk',
      dateCreated: new Date(),
      lastVisited: new Date(),
      isElevated: false,
      setUpForTransfer: false,
      contactHistoryList: contactHistoryListData,
      interactionHistoryList: interactionsHistoryListData,
    },
    {
      id: 845932154,
      customer: {
        externalDataLoyaltyInfoHide: true,
        consumerCardTicketTabHide: true,
        accountNumber: '35265654',
        salutation: 'Ms.',
        title: '',
        firstName: 'Amara',
        middleName: '',
        lastName: 'Kapadia',
        ssn: '15363436',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        customerSecondaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8955',
        emailId: 'KapadiaBRLiam342@sunsyst.com',
        replywithApproval: 'No',
        twitterId: '@AmaraKapadia',
        balance: '$456.98',
        nextPayment: '$50',
        accountLevel: 'Basic',
        customerSince: '2/13/2024',
        dateOfBirth: '12/11/1989',
        customerHistory: [
          {
            id: '1',
            contactId: '253-895-8956',
            contactRequest: 'Airline Customer Service',
            contactTrackingId: '423-INCL-0246',
            contactName: 'Agent Smith',
            contactStatus: 'New',
            digitalChannel: 'digital',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '2',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Question about my luggage',
            contactTrackingId: '78-EMAIL-5S98',
            contactName: 'Agent Kapoor',
            contactStatus: 'Open',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '3',
            contactId: '253-895-8956',
            contactRequest: 'Customer Service',
            contactTrackingId: '897-OTVO-7455',
            contactName: 'Agent Tiwari',
            contactStatus: 'Pending',
            digitalChannel: 'voice',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '4',
            contactId: '253-895-8956',
            contactRequest: 'Cell Phone Number Edit',
            contactTrackingId: '',
            contactName: 'Agent Pablo Fransico',
            contactStatus: 'Resolved',
            digitalChannel: 'voice',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '5',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Tickets Purchased',
            contactTrackingId: '456-EMAIL-6245',
            contactName: 'Agent Winston',
            contactStatus: 'Closed',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '6',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Channel added',
            contactTrackingId: '',
            contactName: 'Agent Tiwari',
            contactStatus: 'Closed',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '7',
            contactId: '360-253-8956',
            contactRequest: 'Customer added',
            contactTrackingId: '',
            contactName: 'Agent Dhaksan',
            contactStatus: 'Escalated',
            digitalChannel: 'contactprofile',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
        ],
        others: {
          guarantorFirstName: 'Amara',
          guarantorLastName: 'Kapadia',
          contactToSpeak: '',
          contactGroup: 'Sales',
          department: 'Pre Sales',
        },
      },
      notes: notesHistoryListData,
      externalData: externalTabDetails,
      recentInteraction: [
        {
          id: 20001,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',

          status: '4 - Low',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/09/2024 08:10 PM',
          recordType: 'current_record',
          details:
            'Customer called in about their hotel reservation to see if they were eligible for a free upgrade.',
        },
        {
          id: 20002,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'related_record',
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20003,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20004,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
      ],
      messageType: 'Post',
      queueName: 'Mention',
      media: MediaType.DIGITAL,
      channel: DigitalChannelType.TWITTER,
      contactStatus: 'new',
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      copilotAssist: 'Yes',
      copilotSource: 'JSON',
      contactButtons: [contactButtons.transfer, contactButtons.commit],
      lastUpdated: '2 hr',
      screenPopURL: 'https://help.nice-incontact.com/content/home.htm#',
      screenPopPreference: 'Off',
      autoSummary: 'Off',
      autoSummaryText:
        'The customer called to request new insurance cards for their daughter, mentioning their employment with the company. The agent confirmed the policy number and offered assistance with ordering the cards. The agent provided information about the delivery timeframe of 10 business days. They also informed the customer about eligibility for a covered annual physical. The issue was resolved successfully.',
      customerChatReply: 'kkkkk',
      crmLabel: 'View CRM Details',
      crmUrl: 'http://www.crm.com',
      dateCreated: new Date(),
      lastVisited: new Date(),
      isElevated: false,
      setUpForTransfer: false,
      contactHistoryList: contactHistoryListData,
      interactionHistoryList: interactionsHistoryListData,
    },
    {
      id: 845932155,
      notes: notesHistoryListData,
      externalData: externalTabDetails,
      recentInteraction: [
        {
          id: 20001,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          status: '4 - Low',
          recordType: 'current_record',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/09/2024 08:32 PM',
          details:
            'Customer called in about their hotel reservation to see if they were eligible for a free upgrade.',
        },
        {
          id: 20002,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20003,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20004,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
      ],
      customer: {
        externalDataLoyaltyInfoHide: true,
        consumerCardTicketTabHide: true,
        accountNumber: '12234456',
        salutation: 'Ms.',
        title: '',
        firstName: 'Caroline',
        middleName: '',
        lastName: 'Rich',
        ssn: '68898989999',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        customerSecondaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8957',
        emailId: 'caroline.rich@sunsyst.com',
        replywithApproval: 'No',
        twitterId: '@CalRich',
        balance: '$456.98',
        nextPayment: '$50',
        accountLevel: 'Basic',
        customerSince: '2/13/2024',
        dateOfBirth: '12/11/1989',
        customerHistory: [
          {
            id: '1',
            contactId: '253-895-8956',
            contactRequest: 'Airline Customer Service',
            contactTrackingId: '423-INCL-0246',
            contactName: 'Agent Smith',
            contactStatus: 'New',
            digitalChannel: 'digital',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '2',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Question about my luggage',
            contactTrackingId: '78-EMAIL-5S98',
            contactName: 'Agent Kapoor',
            contactStatus: 'Open',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '3',
            contactId: '253-895-8956',
            contactRequest: 'Customer Service',
            contactTrackingId: '897-OTVO-7455',
            contactName: 'Agent Tiwari',
            contactStatus: 'Resolved',
            digitalChannel: 'voice',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '4',
            contactId: '253-895-8956',
            contactRequest: 'Cell Phone Number Edit',
            contactTrackingId: '',
            contactName: 'Agent Pablo Fransico',
            contactStatus: 'Resolved',
            digitalChannel: 'voice',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '5',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Tickets Purchased',
            contactTrackingId: '456-EMAIL-6245',
            contactName: 'Agent Winston',
            contactStatus: 'Escalated',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '6',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Channel added',
            contactTrackingId: '',
            contactName: 'Agent Tiwari',
            contactStatus: 'Escalated',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '7',
            contactId: '360-253-8956',
            contactRequest: 'Customer added',
            contactTrackingId: '',
            contactName: 'Agent Dhaksan',
            contactStatus: 'Closed',
            digitalChannel: 'contactprofile',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
        ],
        others: {
          guarantorFirstName: 'Caroline',
          guarantorLastName: 'Rich',
          contactToSpeak: '',
          contactGroup: 'Sales',
          department: 'Pre Sales',
        },
      },
      messageType: 'Chat',
      queueName: 'VIP Client',
      media: MediaType.DIGITAL,
      channel: DigitalChannelType.CHAT,
      channelContent:
        'Is your food service back now or are you still cost cutting? I miss my Biscoff .',
      contactStatus: 'new',
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      copilotAssist: 'Yes',
      copilotSource: 'JSON',
      contactButtons: [
        contactButtons.cobrowsing,
        contactButtons.location,
        contactButtons.consult,
      ],
      lastUpdated: '2 hr',
      screenPopURL: 'https://help.nice-incontact.com/content/home.htm#',
      screenPopPreference: 'Off',
      autoSummary: 'On',
      autoSummaryText:
        'Customer wanted help booking her trip to Iceland. It was booked for June 20th departing out of SLC arriving in KEF. Agent created a 7-day itinerary for her trip that covered all her destinations of choice and sent it to her via email. Customer was satisfied with the resolution of the chat.',
      journeyHistory: false,
      perviousInteractionJsonData: customCopilotTemplate?.journeyHistory,
      rtSummary: customCopilotTemplate?.rtSummary,
      showtransferSummary: false,
      transferSummary: customCopilotTemplate?.agentTransferSummary,
      botTranscriptData: customCopilotTemplate?.botTranscriptData,
      crmLabel: 'View CRM Details',
      crmUrl: 'http://www.crm.com',
      customerChatReply: 'kkkkk',
      dateCreated: new Date(),
      lastVisited: new Date(),
      isElevated: false,
      setUpForTransfer: false,
      contactHistoryList: contactHistoryListData,
      interactionHistoryList: interactionsHistoryListData,
      elevationDetails: new Map(),
      botTranscript: false,
    },
    {
      id: 845932156,
      notes: notesHistoryListData,
      externalData: externalTabDetails,
      recentInteraction: [
        {
          id: 20001,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          status: '4 - Low',
          recordType: 'current_record',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/09/2024 08:32 PM',
          details:
            'Customer called in about their hotel reservation to see if they were eligible for a free upgrade.',
        },
        {
          id: 20002,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'related_record',
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20003,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20004,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
      ],
      customer: {
        externalDataLoyaltyInfoHide: true,
        consumerCardTicketTabHide: true,
        accountNumber: '786756856',
        salutation: 'Ms.',
        title: '',
        firstName: 'Marsa',
        middleName: '',
        lastName: 'Rastova',
        ssn: '899986586',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        customerSecondaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8958',
        emailId: 'RastovaBRLiam342@sunsyst.com',
        replywithApproval: 'No',
        twitterId: '@MarsaRastova',
        balance: '$456.98',
        nextPayment: '$50',
        accountLevel: 'Basic',
        customerSince: '2/13/2024',
        dateOfBirth: '12/11/1989',
        customerHistory: [
          {
            id: '1',
            contactId: '253-895-8956',
            contactRequest: 'Airline Customer Service',
            contactTrackingId: '423-INCL-0246',
            contactName: 'Agent Smith',
            contactStatus: 'New',
            digitalChannel: 'digital',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '2',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Question about my luggage',
            contactTrackingId: '78-EMAIL-5S98',
            contactName: 'Agent Kapoor',
            contactStatus: 'New',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '3',
            contactId: '253-895-8956',
            contactRequest: 'Customer Service',
            contactTrackingId: '897-OTVO-7455',
            contactName: 'Agent Tiwari',
            contactStatus: 'Open',
            digitalChannel: 'voice',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '4',
            contactId: '253-895-8956',
            contactRequest: 'Cell Phone Number Edit',
            contactTrackingId: '',
            contactName: 'Agent Pablo Fransico',
            contactStatus: 'Closed',
            digitalChannel: 'voice',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '5',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Tickets Purchased',
            contactTrackingId: '456-EMAIL-6245',
            contactName: 'Agent Winston',
            contactStatus: 'Resolved',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '6',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Channel added',
            contactTrackingId: '',
            contactName: 'Agent Tiwari',
            contactStatus: 'Resolved',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '7',
            contactId: '360-253-8956',
            contactRequest: 'Customer added',
            contactTrackingId: '',
            contactName: 'Agent Dhaksan',
            contactStatus: 'Closed',
            digitalChannel: 'contactprofile',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
        ],
        others: {
          guarantorFirstName: 'Marsa',
          guarantorLastName: 'Rastova',
          contactToSpeak: '',
          contactGroup: 'Banking',
          department: 'Customer Service',
        },
      },
      messageType: 'Public Comment',
      queueName: 'Cust Inquiry',
      media: MediaType.DIGITAL,
      channel: DigitalChannelType.INSTAGRAM,
      contactStatus: 'new',
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      copilotAssist: 'Yes',
      copilotSource: 'JSON',
      contactButtons: [contactButtons.transfer, contactButtons.commit],
      lastUpdated: '2 hr',
      screenPopURL: 'https://help.nice-incontact.com/content/home.htm#',
      screenPopPreference: 'Off',
      autoSummary: 'Off',
      autoSummaryText:
        'The customer called to request new insurance cards for their daughter, mentioning their employment with the company. The agent confirmed the policy number and offered assistance with ordering the cards. The agent provided information about the delivery timeframe of 10 business days. They also informed the customer about eligibility for a covered annual physical. The issue was resolved successfully.',
      customerChatReply: 'kkkkk',
      crmLabel: 'View CRM Details',
      crmUrl: 'http://www.crm.com',
      dateCreated: new Date(),
      lastVisited: new Date(),
      isElevated: false,
      setUpForTransfer: false,
      contactHistoryList: contactHistoryListData,
      interactionHistoryList: interactionsHistoryListData,
    },
    {
      id: 845932157,
      notes: notesHistoryListData,
      externalData: externalTabDetails,
      recentInteraction: [
        {
          id: 20001,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'current_record',
          status: '4 - Low',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/09/2024 08:32 PM',
          details:
            'Customer called in about their hotel reservation to see if they were eligible for a free upgrade.',
        },
        {
          id: 20002,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'related_record',
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20003,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20004,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
      ],
      customer: {
        externalDataLoyaltyInfoHide: true,
        consumerCardTicketTabHide: true,
        accountNumber: '99696565',
        salutation: 'Mr.',
        title: '',
        firstName: 'Sam',
        middleName: '',
        lastName: 'keen',
        ssn: '134356677',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        customerSecondaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8959',
        emailId: 'keenBRLiam342@sunsyst.com',
        replywithApproval: 'No',
        twitterId: '@Samkeen',
        balance: '$456.98',
        nextPayment: '$50',
        accountLevel: 'Basic',
        customerSince: '2/13/2024',
        dateOfBirth: '12/11/1989',
        customerHistory: [
          {
            id: '1',
            contactId: '253-895-8956',
            contactRequest: 'Airline Customer Service',
            contactTrackingId: '423-INCL-0246',
            contactName: 'Agent Smith',
            contactStatus: 'Open',
            digitalChannel: 'digital',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '2',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Question about my luggage',
            contactTrackingId: '78-EMAIL-5S98',
            contactName: 'Agent Kapoor',
            contactStatus: 'Closed',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '3',
            contactId: '253-895-8956',
            contactRequest: 'Customer Service',
            contactTrackingId: '897-OTVO-7455',
            contactName: 'Agent Tiwari',
            contactStatus: 'New',
            digitalChannel: 'voice',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '4',
            contactId: '253-895-8956',
            contactRequest: 'Cell Phone Number Edit',
            contactTrackingId: '',
            contactName: 'Agent Pablo Fransico',
            contactStatus: 'Pending',
            digitalChannel: 'voice',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '5',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Tickets Purchased',
            contactTrackingId: '456-EMAIL-6245',
            contactName: 'Agent Winston',
            contactStatus: 'Resolved',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '6',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Channel added',
            contactTrackingId: '',
            contactName: 'Agent Tiwari',
            contactStatus: 'Resolved',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '7',
            contactId: '360-253-8956',
            contactRequest: 'Customer added',
            contactTrackingId: '',
            contactName: 'Agent Dhaksan',
            contactStatus: 'Escalated',
            digitalChannel: 'contactprofile',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
        ],
        others: {
          guarantorFirstName: 'Sam',
          guarantorLastName: 'Keen',
          contactToSpeak: '',
          contactGroup: 'Finance',
          department: 'Customer Service',
        },
      },
      messageType: 'Post',
      queueName: 'Mention',
      media: MediaType.DIGITAL,
      contactStatus: 'new',
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      copilotAssist: 'Yes',
      copilotSource: 'JSON',
      channel: DigitalChannelType.TWITTER,
      agentAssistStatus: AgentAssistStatus.NEGATIVE,
      contactButtons: [contactButtons.transfer, contactButtons.commit],
      lastUpdated: '2 hr',
      screenPopURL: 'https://help.nice-incontact.com/content/home.htm#',
      screenPopPreference: 'Off',
      autoSummary: 'Off',
      autoSummaryText:
        'The customer called to request new insurance cards for their daughter, mentioning their employment with the company. The agent confirmed the policy number and offered assistance with ordering the cards. The agent provided information about the delivery timeframe of 10 business days. They also informed the customer about eligibility for a covered annual physical. The issue was resolved successfully.',
      customerChatReply: 'kkkkk',
      crmLabel: 'View CRM Details',
      crmUrl: 'http://www.crm.com',
      dateCreated: new Date(),
      lastVisited: new Date(),
      isElevated: false,
      setUpForTransfer: false,
      contactHistoryList: contactHistoryListData,
      interactionHistoryList: interactionsHistoryListData,
    },
    {
      id: 845932158,
      notes: notesHistoryListData,
      externalData: externalTabDetails,
      recentInteraction: [
        {
          id: 20001,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'current_record',
          status: '4 - Low',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/09/2024 08:32 PM',
          details:
            'Customer called in about their hotel reservation to see if they were eligible for a free upgrade.',
        },
        {
          id: 20002,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20003,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20004,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'related_record',
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
      ],
      customer: {
        externalDataLoyaltyInfoHide: true,
        consumerCardTicketTabHide: true,
        accountNumber: '67587687',
        salutation: 'Mr.',
        title: '',
        firstName: 'Eric',
        middleName: '',
        lastName: 'Dane',
        ssn: '879879677',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        customerSecondaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8960',
        emailId: 'DavisBRLiam342@sunsyst.com',
        replywithApproval: 'No',
        twitterId: '@EricDane',
        balance: '$456.98',
        nextPayment: '$50',
        accountLevel: 'Basic',
        customerSince: '2/13/2024',
        dateOfBirth: '12/11/1989',
        customerHistory: [
          {
            id: '1',
            contactId: '253-895-8956',
            contactRequest: 'Airline Customer Service',
            contactTrackingId: '423-INCL-0246',
            contactName: 'Agent Smith',
            contactStatus: 'Open',
            digitalChannel: 'digital',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '2',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Question about my luggage',
            contactTrackingId: '78-EMAIL-5S98',
            contactName: 'Agent Kapoor',
            contactStatus: 'New',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '3',
            contactId: '253-895-8956',
            contactRequest: 'Customer Service',
            contactTrackingId: '897-OTVO-7455',
            contactName: 'Agent Tiwari',
            contactStatus: 'Pending',
            digitalChannel: 'voice',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '4',
            contactId: '253-895-8956',
            contactRequest: 'Cell Phone Number Edit',
            contactTrackingId: '',
            contactName: 'Agent Pablo Fransico',
            contactStatus: 'Pending',
            digitalChannel: 'voice',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '5',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Tickets Purchased',
            contactTrackingId: '456-EMAIL-6245',
            contactName: 'Agent Winston',
            contactStatus: 'Escalated',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '6',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Channel added',
            contactTrackingId: '',
            contactName: 'Agent Tiwari',
            contactStatus: 'Escalated',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '7',
            contactId: '360-253-8956',
            contactRequest: 'Customer added',
            contactTrackingId: '',
            contactName: 'Agent Dhaksan',
            contactStatus: 'Closed',
            digitalChannel: 'contactprofile',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
        ],
        others: {
          guarantorFirstName: 'Eric',
          guarantorLastName: 'Dane',
          contactToSpeak: '',
          contactGroup: 'Banking',
          department: 'Customer Service',
        },
      },
      messageType: 'Post',
      queueName: 'Mention',
      media: MediaType.DIGITAL,
      channel: DigitalChannelType.CHAT,
      channelContent:
        'Is your food service back now or are you still cost cutting? I miss my Biscoff cookies.',
      contactStatus: 'new',
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      copilotAssist: 'Yes',
      copilotSource: 'JSON',
      contactButtons: [contactButtons.transfer, contactButtons.commit],
      lastUpdated: '2 hr',
      screenPopURL: 'https://help.nice-incontact.com/content/home.htm#',
      screenPopPreference: 'Off',
      autoSummary: 'Off',
      autoSummaryText:
        'The customer called to request new insurance cards for their daughter, mentioning their employment with the company. The agent confirmed the policy number and offered assistance with ordering the cards. The agent provided information about the delivery timeframe of 10 business days. They also informed the customer about eligibility for a covered annual physical. The issue was resolved successfully.',
      customerChatReply: 'kkkkk',
      crmLabel: 'View CRM Details',
      crmUrl: 'http://www.crm.com',
      dateCreated: new Date(),
      lastVisited: new Date(),
      isElevated: false,
      setUpForTransfer: false,
      contactHistoryList: contactHistoryListData,
      interactionHistoryList: interactionsHistoryListData,
    },
    {
      id: 945932156,
      notes: notesHistoryListData,
      externalData: externalTabDetails,
      recentInteraction: [
        {
          id: 20001,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          status: '4 - Low',
          recordType: 'current_record',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/09/2024 08:32 PM',
          details:
            'Customer called in about their hotel reservation to see if they were eligible for a free upgrade.',
        },
        {
          id: 20002,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'related_record',
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20003,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20004,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
      ],
      customer: {
        externalDataLoyaltyInfoHide: true,
        consumerCardTicketTabHide: true,
        accountNumber: '768756556',
        salutation: 'Mr.',
        title: '',
        firstName: 'Davis',
        middleName: '',
        lastName: 'Briliam',
        ssn: '222324455',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        customerSecondaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8958',
        emailId: 'davisbriliam@sunsyst.com',
        replywithApproval: 'No',
        twitterId: '@DavisBriliam',
        balance: '$456.98',
        nextPayment: '$50',
        accountLevel: 'Basic',
        customerSince: '2/13/2024',
        dateOfBirth: '12/11/1989',
        customerHistory: [
          {
            id: '1',
            contactId: '253-895-8956',
            contactRequest: 'Airline Customer Service',
            contactTrackingId: '423-INCL-0246',
            contactName: 'Agent Smith',
            contactStatus: 'New',
            digitalChannel: 'digital',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '2',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Question about my luggage',
            contactTrackingId: '78-EMAIL-5S98',
            contactName: 'Agent Kapoor',
            contactStatus: 'New',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '3',
            contactId: '253-895-8956',
            contactRequest: 'Customer Service',
            contactTrackingId: '897-OTVO-7455',
            contactName: 'Agent Tiwari',
            contactStatus: 'New',
            digitalChannel: 'voice',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '4',
            contactId: '253-895-8956',
            contactRequest: 'Cell Phone Number Edit',
            contactTrackingId: '',
            contactName: 'Agent Pablo Fransico',
            contactStatus: 'Open',
            digitalChannel: 'voice',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '5',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Tickets Purchased',
            contactTrackingId: '456-EMAIL-6245',
            contactName: 'Agent Winston',
            contactStatus: 'Open',
            digitalChannel: 'email',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '6',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Channel added',
            contactTrackingId: '',
            contactName: 'Agent Tiwari',
            contactStatus: 'Open',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '7',
            contactId: '360-253-8956',
            contactRequest: 'Customer added',
            contactTrackingId: '',
            contactName: 'Agent Dhaksan',
            contactStatus: 'Closed',
            digitalChannel: 'contactprofile',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
        ],
        others: {
          guarantorFirstName: 'Davis',
          guarantorLastName: 'Briliam',
          contactToSpeak: '',
          contactGroup: 'Travel',
          department: 'Customer Service',
        },
      },
      messageType: 'Email',
      queueName: 'RE: Where is my refund? i cannot beleive',
      emailSubject: 'RE: Where is my refund? i cannot beleive',
      receiverEmail: 'davisbriliam@sunsyst.com',
      media: MediaType.DIGITAL,
      channel: DigitalChannelType.EMAIL,
      contactStatus: 'new',
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      copilotAssist: 'Yes',
      copilotSource: 'JSON',
      contactButtons: [contactButtons.commit],
      lastUpdated: '2 hr',
      screenPopURL: 'https://help.nice-incontact.com/content/home.htm#',
      screenPopPreference: 'Off',
      autoSummary: 'Off',
      autoSummaryText:
        'The customer called to request new insurance cards for their daughter, mentioning their employment with the company. The agent confirmed the policy number and offered assistance with ordering the cards. The agent provided information about the delivery timeframe of 10 business days. They also informed the customer about eligibility for a covered annual physical. The issue was resolved successfully.',
      rtSummary: customCopilotEmailTemplate?.rtSummary,
      journeyHistory: false,
      perviousInteractionJsonData: customCopilotEmailTemplate?.journeyHistory,
      showtransferSummary: false,
      transferSummary: customCopilotEmailTemplate?.agentTransferSummary,
      customerChatReply: 'kkkkk',
      crmLabel: 'View CRM Details',
      crmUrl: 'http://www.crm.com',
      dateCreated: new Date(),
      lastVisited: new Date(),
      isElevated: false,
      setUpForTransfer: false,
      contactHistoryList: contactHistoryListData,
      interactionHistoryList: interactionsHistoryListData,
    },
    {
      id: 845932167,
      notes: notesHistoryListData,
      externalData: externalTabDetails,
      recentInteraction: [
        {
          id: 20001,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          status: '4 - Low',
          recordType: 'current_record',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/09/2024 08:32 PM',
          details:
            'Customer called in about their hotel reservation to see if they were eligible for a free upgrade.',
        },
        {
          id: 20002,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          isCopy: true,
          recordType: 'related_record',
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20003,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
        {
          id: 20004,
          crmIcon: '/assets/nowCase.png',
          crmUrl: 'http://www.crm.com',
          recordType: 'related_record',
          isCopy: true,
          status: 'Closed',
          label: `caseID,${Math.floor(Math.random() * 900) + 100}`,
          date: '03/02/2024 12:54 PM',
          details:
            'Customer called in about their hotel reservation to add valet parking.',
        },
      ],
      customer: {
        externalDataLoyaltyInfoHide: true,
        consumerCardTicketTabHide: true,
        accountNumber: '76875658',
        salutation: 'Mr.',
        title: '',
        firstName: 'Paul',
        middleName: '',
        lastName: 'Adams',
        ssn: '758676785',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        customerSecondaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          state: '',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8976',
        emailId: 'PaulAdams@sunsyst.com',
        replywithApproval: 'No',
        twitterId: '@AdamsPaul',
        balance: '$456.98',
        nextPayment: '$50',
        accountLevel: 'Basic',
        customerSince: '2/13/2024',
        dateOfBirth: '12/11/1989',
        customerHistory: [
          {
            id: '0',
            contactId: '253-895-8956',
            contactRequest: 'Airline Customer Service',
            contactTrackingId: '423-INCL-0246',
            contactName: 'Agent Smith',
            contactStatus: 'New',
            digitalChannel: 'digital',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '1',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Question about my luggage',
            contactTrackingId: '78-EMAIL-5S98',
            contactName: 'Agent Kapoor',
            contactStatus: 'Open',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '2',
            contactId: '253-895-8956',
            contactRequest: 'Customer Service',
            contactTrackingId: '897-OTVO-7455',
            contactName: 'Agent Tiwari',
            contactStatus: 'Closed',
            digitalChannel: 'voice',
            isOutbound: false,
            updatedDate: '13/10/2021',
          },
          {
            id: '3',
            contactId: '253-895-8956',
            contactRequest: 'Cell Phone Number Edit',
            contactTrackingId: '',
            contactName: 'Agent Pablo Fransico',
            contactStatus: 'Pending',
            digitalChannel: 'voice',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '4',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Tickets Purchased',
            contactTrackingId: '456-EMAIL-6245',
            contactName: 'Agent Winston',
            contactStatus: 'Resolved',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '5',
            contactId: 'DavisBRLiam342@sunsyst.com',
            contactRequest: 'Channel added',
            contactTrackingId: '',
            contactName: 'Agent Tiwari',
            contactStatus: 'Closed',
            digitalChannel: 'email',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
          {
            id: '6',
            contactId: '360-253-8956',
            contactRequest: 'Customer added',
            contactTrackingId: '',
            contactName: 'Agent Dhaksan',
            contactStatus: 'Resolved',
            digitalChannel: 'contactprofile',
            isOutbound: true,
            updatedDate: '13/10/2021',
          },
        ],
        others: {
          guarantorFirstName: 'Paul',
          guarantorLastName: 'Adam',
          contactToSpeak: '',
          contactGroup: 'Sales',
          department: 'Pre Sales',
        },
      },
      messageType: '253-895-8976',
      queueName: 'Cust. Service Request',
      media: MediaType.VOICEMAIL,
      outBoundInitiatedForVoiceMail: false,
      contactStatus: VoiceContactStatus.RINGING,
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      copilotAssist: 'Yes',
      copilotSource: 'JSON',
      agentAssistStatus: AgentAssistStatus.NEGATIVE,
      contactButtons: [
        contactButtons.outBoundCall,
        contactButtons.transfer,
        contactButtons.commitment,
      ],
      screenPopURL: 'https://help.nice-incontact.com/content/home.htm#',
      screenPopPreference: 'Off',
      autoSummary: 'Off',
      autoSummaryText:
        'The customer called to request new insurance cards for their daughter, mentioning their employment with the company. The agent confirmed the policy number and offered assistance with ordering the cards. The agent provided information about the delivery timeframe of 10 business days. They also informed the customer about eligibility for a covered annual physical. The issue was resolved successfully.',
      customerChatReply: 'kkkkk',
      crmLabel: 'View CRM Details',
      crmUrl: 'http://www.crm.com',
      dateCreated: new Date(),
      lastVisited: new Date(),
      isElevated: false,
      setUpForTransfer: false,
      contactHistoryList: contactHistoryListData,
      interactionHistoryList: interactionsHistoryListData,
    },
  ],

  selectedContactIndex: 0,
  modalMessage: '',
  agentDirectory: {
    myTeam: [
      {
        id: 1,
        name: 'Gokul Sadanandam',
        status: 'busy' as AgentStatus.BUSY,
        designation: 'Technical Support Agent',
        contactNo: '+212-111-646',
      },
      {
        id: 2,
        name: 'John Doe',
        status: 'available' as AgentStatus.AVAILABLE,
        designation: 'Technical Support Agent',
      },
      {
        id: 3,
        name: 'Angus L',
        status: 'working' as AgentStatus.WORKING,
        designation: 'Technical Support Agent',
      },
      {
        id: 4,
        name: 'Brandy',
        status: 'offline' as AgentStatus.OFFLINE,
        designation: 'Technical Support Agent',
      },
      {
        id: 5,
        name: 'Greg',
        status: 'available' as AgentStatus.AVAILABLE,
        designation: 'Technical Support Agent',
      },
    ],
    favourites: [
      {
        id: 101,
        name: 'Gokul Sadanandam',
        status: 'available' as AgentStatus.AVAILABLE,
        designation: 'Technical Support Agent',
      },
      {
        id: 111,
        name: 'John Doe',
        status: 'available' as AgentStatus.AVAILABLE,
        designation: 'Technical Support Agent',
      },
    ],
    people: [],
    agentGroups: [
      {
        id: 11,
        name: 'Cust. Service Support',
        status: 'available' as AgentStatus.AVAILABLE,
        designation: 'Technical Support Agent',
      },
      {
        id: 12,
        name: 'Technical Support',
        status: 'available' as AgentStatus.AVAILABLE,
        designation: 'Technical Support Agent',
      },
      {
        id: 13,
        name: 'Sales',
        status: 'available' as AgentStatus.AVAILABLE,
        designation: 'Technical Support Agent',
      },
    ],
  },
  quickReplies: {
    allQReplies: [
      {
        id: 1,
        title: 'Opening',
        content1:
          'Hello, my name is Nick Lewis. Please allow me a moment to review the notes on your account. ',
        isfavorite: true,
        isConfirmationNumberReq: false,
      },
      {
        id: 2,
        title: 'Closing',
        content1:
          'Thank you for trusting Synchrony Financial with financial needs, have a great day.',
        isfavorite: true,
        isConfirmationNumberReq: false,
      },
      {
        id: 3,
        title: 'Research',
        content1: 'Please give me a moment to research your question.',
        isfavorite: true,
        isConfirmationNumberReq: false,
      },
      {
        id: 4,
        title: 'Late Fee',
        content1:
          'As a one-time courtesy, I am able to refuse your $35 late fee.',
        isfavorite: true,
        isConfirmationNumberReq: false,
      },
      {
        id: 5,
        title: 'Payment confirmation',
        content1:
          'Please confirm a payment of $35 will be made to your account today. ',
        isfavorite: false,
        isConfirmationNumberReq: false,
      },
    ],
  },
  expertSystemOptions: {},
};

export const simulatorSlice = createSlice({
  name: SIMULATOR_FEATURE_KEY,
  initialState: initialSimulatorState,
  reducers: {
    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    clearAllSimulatorData(state) {
      return {
        ...state,
        contact: initialSimulatorState.contact,
        selectedContactIndex: 0,
        modalMessage: '',
        agentDirectory: initialSimulatorState.agentDirectory,
        quickReplies: initialSimulatorState.quickReplies,
        expertSystemOptions: initialSimulatorState.expertSystemOptions,
      };
    },
    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateContactSelection(state, action: PayloadAction<number>) {
      return {
        ...state,
        selectedContactIndex: action.payload,
      };
    },
    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateCurrentInteraction(state: any, action: PayloadAction<any>) {
      const currentContact: Contact[] = [...state.contact];
      const currentUpdatedContact = {
        ...currentContact[state.selectedContactIndex],
        recentInteraction: action.payload,
      };
      currentContact[state.selectedContactIndex] = currentUpdatedContact;

      return {
        ...state,
        contact: currentContact,
      };
    },
    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateselectedContact(
      state,
      action: PayloadAction<{ field: string; value: any }>
    ) {
      const { field, value } = action.payload;
      const currentContact: Contact[] = [...state.contact];
      const customerFields = [
        'customerName',
        'customerAddress',
        'phoneNo',
        'replywithApproval',
        'twitterId',
        'firstName',
        'lastName',
        'emailId'
      ];
      const currentUpdatedContact: Contact = customerFields.includes(field)
        ? {
            ...currentContact[state.selectedContactIndex],
            customer: {
              ...currentContact[state.selectedContactIndex].customer,
              [field]: value,
            },
          }
        : { ...currentContact[state.selectedContactIndex], [field]: value };
      if (field === 'channel' && value === 'Email') {
        currentUpdatedContact.contactButtons = [contactButtons.commit];
        currentUpdatedContact.messageType = 'Email';
        currentUpdatedContact.queueName =
          'RE: Where is my refund? i cannot beleive';
      } else if (field === 'media' && value === 'Digital') {
        currentUpdatedContact.channel = 'Email';
      } else if (
        (field === 'customerChatReply' ||
          field === 'customerChatReplyJsonData' ||
          (field === 'channel' && value === 'Chat')) &&
        currentUpdatedContact.media !== MediaType.VOICE
      ) {
        currentUpdatedContact.contactButtons = [contactButtons.commit];
        currentUpdatedContact.messageType = 'Chat';
        currentUpdatedContact.queueName = '';
      } else {
        if (currentUpdatedContact.media === MediaType.DIGITAL) {
          if (
            field === 'channel' &&
            value === 'Chat' &&
            currentUpdatedContact?.copilotAssist === 'Yes'
          ) {
            currentUpdatedContact.contactButtons = [
              contactButtons.cobrowsing,
              contactButtons.commit,
              contactButtons.location,
            ];
            currentUpdatedContact.messageType = 'Chat';
            currentUpdatedContact.queueName = 'VIP Client';
          } else {
            currentUpdatedContact.messageType = 'Post';
            currentUpdatedContact.queueName = 'Mention';
          }
        }
      }
      if (currentUpdatedContact.media === MediaType.VOICE) {
        currentUpdatedContact.contactButtons = [
          contactButtons.hold,
          contactButtons.mask,
          contactButtons.mute,
          contactButtons.keypad,
          contactButtons.transfer,
          contactButtons.record,
          //contactButtons.commit,
          contactButtons.hungup,
        ];
      } else if (currentUpdatedContact.media === MediaType.DIGITAL) {
        currentUpdatedContact.contactButtons = [
          contactButtons.transfer,
          contactButtons.commit,
        ];
      } else if (currentUpdatedContact.media === MediaType.VIDEOCALL) {
        currentUpdatedContact.channel = 'VideoCall';
        currentUpdatedContact.messageType = 'VideoCall';
        currentUpdatedContact.queueName = 'Damaged Luggage';
      }
      currentContact[state.selectedContactIndex] = currentUpdatedContact;
      return {
        ...state,
        contact: currentContact,
        customer: {
          ...currentContact[state.selectedContactIndex].customer,
          ...action.payload,
        },
      };
    },

    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateselectedCustomer(
      state,
      action: PayloadAction<{ field: string; value: any }>
    ) {
      const currentContact: Contact[] = [...state.contact];
      const currentUpdatedContact = {
        ...currentContact[state.selectedContactIndex],
        customer: {
          ...currentContact[state.selectedContactIndex].customer,
          ...action.payload,
        },
      };

      currentContact[state.selectedContactIndex] = currentUpdatedContact;
      return {
        ...state,
        contact: currentContact,
      };
    },

    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateselectedContactHistory(
      state,
      action: PayloadAction<{ id: any; field: string; value: any }>
    ) {
      const { id, field, value } = action.payload;
      const currentContacts: any[] = [...state.contact];
      const updatedContactHistory: any =
        field === 'customer' ||
        field === 'tags' ||
        field === 'skill' ||
        field === 'status' ||
        field === 'disposition' ||
        field === 'notes' ||
        field === 'time' ||
        field === 'emailId'
          ? {
              ...currentContacts[state.selectedContactIndex],
              contactHistoryList: [
                ...currentContacts[
                  state.selectedContactIndex
                ].contactHistoryList.map((el: any) =>
                  el.id === id ? { ...el, [field]: value } : { ...el }
                ),
              ],
            }
          : { ...currentContacts[state.selectedContactIndex], [field]: value };
      // currentContact[state.selectedContactIndex] = updatedContactHistory;
      const updatedContacts = currentContacts.map((contactEle: any) => {
        return {
          ...contactEle,
          contactHistoryList: updatedContactHistory.contactHistoryList,
        };
      });
      return {
        ...state,
        contact: updatedContacts,
      };
    },

    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateselectedInteractionHistory(
      state,
      action: PayloadAction<{ id: any; field: string; value: any }>
    ) {
      const { id, field, value } = action.payload;
      const currentContacts: any[] = [...state.contact];
      const updatedInteractionHistory: any =
        field === 'date' ||
        field === 'media' ||
        field === 'status' ||
        field === 'endTime' ||
        field === 'contactTrackingId' ||
        field === 'contactRequest' ||
        field === 'agentName' ||
        field === 'skill' ||
        field === 'campaign' ||
        field === 'disposition' ||
        field === 'autoSummary'
          ? {
              ...currentContacts[state.selectedContactIndex],
              interactionHistoryList: [
                ...currentContacts[
                  state.selectedContactIndex
                ].interactionHistoryList.map((el: any) =>
                  el.interactionId === id
                    ? { ...el, [field]: value }
                    : { ...el }
                ),
              ],
            }
          : { ...currentContacts[state.selectedContactIndex], [field]: value };
      // currentContact[state.selectedContactIndex] = updatedContactHistory;
      const updatedContacts = currentContacts.map((contactEle: any) => {
        return {
          ...contactEle,
          interactionHistoryList:
            updatedInteractionHistory.interactionHistoryList,
        };
      });
      return {
        ...state,
        contact: updatedContacts,
      };
    },

    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateselectedAgentHistory(
      state,
      action: PayloadAction<{ id: string; field: string; value: string }>
    ) {
      const { id, field, value } = action.payload;
      const currentContact: any[] = [...state.contact];
      const currentUpdatedContactHistory: any =
        field === 'contactName' ||
        field === 'contactId' ||
        field === 'contactTrackingId' ||
        field === 'contactRequest' ||
        field === 'contactStatus' ||
        field === 'digitalChannel'
          ? {
              ...currentContact[state.selectedContactIndex],
              customer: {
                ...currentContact[state.selectedContactIndex].customer,
                customerHistory: [
                  ...currentContact[
                    state.selectedContactIndex
                  ].customer.customerHistory.map((el: any) =>
                    el.id === id ? { ...el, [field]: value } : { ...el }
                  ),
                ],
              },
            }
          : { ...currentContact[state.selectedContactIndex], [field]: value };
      currentContact[state.selectedContactIndex] = currentUpdatedContactHistory;
      return {
        ...state,
        contact: currentContact,
      };
    },
    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateChannelData(
      state,
      action: PayloadAction<{ field: string; value: string }>
    ) {
      const { field, value } = action.payload;
      const currentContact: Contact[] = [...state.contact];
      const currentUpdatedContact: Contact =
        field === 'phoneNo' || field === 'emailID' || field === 'twitterId'
          ? {
              ...currentContact[state.selectedContactIndex],
              customer: {
                ...currentContact[state.selectedContactIndex].customer,
                [field]: value,
              },
            }
          : { ...currentContact[state.selectedContactIndex], [field]: value };
      currentContact[state.selectedContactIndex] = currentUpdatedContact;
      return {
        ...state,
        contact: currentContact,
      };
    },
    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateCustomerChatJSONData(state, action: any) {
      return {
        ...state,
        customerChatInputReply: action.payload,
      };
    },
    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateExpertSystemOptions(state, action: any) {
      return {
        ...state,
        expertSystemOptions: {
          ...state.expertSystemOptions,
          ...action.payload,
        },
      };
    },
  },
});

/*
 * Export reducer for store configuration.
 */
export const simulatorActions = simulatorSlice.actions;
export const simulatorReducer = simulatorSlice.reducer;

export const {
  updateContactSelection,
  updateCurrentInteraction,
  updateselectedContact,
  updateselectedContactHistory,
  updateselectedInteractionHistory,
  updateselectedAgentHistory,
  updateCustomerChatJSONData,
  updateExpertSystemOptions,
  updateselectedCustomer,
  clearAllSimulatorData,
} = simulatorSlice.actions;

/**
 *
 * @param rootState -
 * @example -
 * @returns
 */
const getSimulatorState = (rootState: { simulator: SimulatorState }) => {
  return rootState[SIMULATOR_FEATURE_KEY];
};

export const contactState = createSelector(
  getSimulatorState,
  (state) => state.contact
);
export const contactSelectedIndexState = createSelector(
  getSimulatorState,
  (state) => state.selectedContactIndex
);
export const modalMessageState = createSelector(
  getSimulatorState,
  (state) => state.modalMessage
);
export const customerChatInputReplyState = createSelector(
  getSimulatorState,
  (state) => state.customerChatInputReply
);

export const getExpertSystemOptions = createSelector(
  getSimulatorState,
  (state) => state.expertSystemOptions
);
